import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn } from '@/components/ui/data-table'
import { DateCell, ListCell } from '@/components/TableCells'
import type { ReleaseNote } from '@/services/releaseNotes/releaseNotesApi'

interface ReleaseNoteTableProps {
  notes: ReleaseNote[]
  loading?: boolean
  emptyMessage?: string
}

export function ReleaseNoteTable({ notes, loading, emptyMessage }: ReleaseNoteTableProps) {
  const { t } = useTranslation()

  const columns: ColumnDef<ReleaseNote, unknown>[] = [
    {
      accessorKey: 'title',
      header: t('releaseNotes.noteTitle'),
      meta: { minWidth: 240 },
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold">{row.original.title}</span>
          {row.original.subTitle && (
            <span className="text-[12px] text-(--ad-text-muted)">{row.original.subTitle}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'publishTime',
      header: t('releaseNotes.publishTime'),
      size: 150,
      cell: ({ row }) => <DateCell value={row.original.publishTime} withTime />,
    },
    {
      id: 'visibility',
      accessorFn: (note) => (note.userGroups.length ? 'INTERNAL' : 'PUBLIC'),
      header: t('releaseNotes.visibility'),
      size: 110,
      cell: ({ getValue }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">
          {t(`releaseNotes.visibilities.${getValue() as string}`)}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: t('common.status'),
      size: 120,
      cell: ({ row }) => (
        <span
          className={`chip chip--${row.original.status === 'PUBLISHED' ? 'running' : 'draft'}`}
          style={{ fontSize: 11 }}
        >
          <span className="dot" />
          {t(`releaseNotes.statuses.${row.original.status}`)}
        </span>
      ),
    },
    {
      accessorKey: 'apps',
      header: t('releaseNotes.apps'),
      enableSorting: false,
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <ListCell values={row.original.apps.map((app) => t(`releaseNotes.appNames.${app}`))} />
      ),
    },
    {
      accessorKey: 'tags',
      header: t('releaseNotes.tags'),
      enableSorting: false,
      meta: { minWidth: 140 },
      cell: ({ row }) => (
        <ListCell values={row.original.tags.map((tag) => t(`releaseNotes.tagNames.${tag}`))} />
      ),
    },
    optionalColumn<ReleaseNote>({
      accessorKey: 'author',
      header: t('releaseNotes.author'),
      meta: { minWidth: 160 },
      cell: ({ row }) => (
        <span className="text-[12.5px] text-(--ad-text-muted)">{row.original.author || '—'}</span>
      ),
    }),
    optionalColumn<ReleaseNote>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
    optionalColumn<ReleaseNote>({
      accessorKey: 'createTime',
      header: t('common.created'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.createTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={notes}
      loading={loading}
      emptyMessage={emptyMessage ?? t('releaseNotes.noReleaseNotesFound')}
      columnsKey="releaseNotes"
      rowHref={(row) => `/super-admin/release-notes/${row.id}`}
    />
  )
}
